import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { useGetAdminSurveysQuery } from "../../redux/admin/survey/survey.endpoints";
import { ProgressSpinner } from 'primereact/progressspinner';
import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import dayjs from 'dayjs';
import { Survey } from '../../redux/admin/survey/survey.types';
import './data-table-list.css';
import { useSetAdminPageTitle } from "../../hooks/useSetPageTitle";
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { B2BButton } from 'src/components/b2bbutton/b2bbutton';

function SurveyListScreen() {
  useSetAdminPageTitle('Surveys');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const { data, refetch } = useGetAdminSurveysQuery({ page, page_size: pageSize, query: searchValue }, { refetchOnMountOrArgChange: true });
  const DATE_TIME_FORMAT = 'MM/DD/YY h:mm a';

  const surveys = data?.surveys;
  const totalObjects = data?.totalObjects;
  const nav = useNavigate();
  useEffect(() => void refetch(), []);

  function onRowClick(event: DataTableRowClickEvent) {
    nav('/admin/surveys/' + event.data.extSurveyId);
  }

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const nextPage = Math.ceil((event.first / pageSize) + 1);
    setPage(nextPage);
    setPageSize(event.rows);
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const formatDate = (date?: string) => {
    if (date)
      return dayjs(date).format(DATE_TIME_FORMAT);
    else
      return '';
  };

  const createdDateTemplate = (survey: Survey) => {
    return formatDate(survey.createdAt);
  };

  const startDateTemplate = (survey: Survey) => {
    return formatDate(survey.startDate);
  };

  const endDateTemplate = (survey: Survey) => {
    return formatDate(survey.endDate);
  };

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    setPage(1);
    setSearchValue(inputValue);
  };

  const onSearchPress = () => {
    setPage(1);
    setSearchValue(inputValue);
  };

  const renderSearchInput = (): ReactElement => {
    return (
      <div className="flex flex-row items-center">
        <IconField className="mr-4">
          <InputIcon className="pi pi-search" />
          <InputText
            className='border-2 rounded-md p-2 w-96'
            value={inputValue}
            onChange={onSearchChange}
            onKeyDown={onKeyPress}
            placeholder="Search by title, creator email, or ext survey id"
          />
        </IconField>
        <B2BButton
          label="Search"
          onClick={onSearchPress}
          size="small"
        />
      </div>
    );
  };

  return (
    <Card
      title="Surveys"
      className='m-8 rounded-lg drop-shadow-lg'
    >
      {data && (
        <div className="card">
          <DataTable
            size="small"
            stripedRows
            value={surveys}
            style={{ cursor: 'pointer' }}
            tableStyle={{ minWidth: '50rem' }}
            onRowClick={onRowClick}
            filterDisplay='menu'
            header={renderSearchInput}
            className='border-2'
          >
            <Column
              field="createdAt"
              header="Created"
              body={createdDateTemplate}
              sortable
            />
            <Column
              field="title"
              header="Title"
              sortable
              filter
            />
            <Column
              field="category"
              header="Category"
              sortable
              filter
            />
            <Column
              field="completedCount"
              header="Completed Users"
              sortable
            />
            {/* <Column field="attemptedCount" header="Total Users" sortable></Column> */}
            {/* <Column field="percentCompleted" header="% Complete" sortable></Column> */}
            <Column
              field="points"
              header="Points"
              sortable
            />
            <Column
              field="rank"
              header="Rank"
              sortable
            />
            <Column
              field="startDate"
              header="Start Date"
              body={startDateTemplate}
              sortable
            />
            <Column
              field="endDate"
              header="End Date"
              body={endDateTemplate}
              sortable
            />
            <Column
              field="isPublished"
              header="Published"
              sortable
            />
          </DataTable>
          <Paginator
            first={(page - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalObjects}
            rowsPerPageOptions={[25, 50, 100, 500]}
            onPageChange={onPageChange}
          />

        </div>
      )}
      {!data && (
        <div className="m-4">
          <div className="grid">
            <div className="col">
              <ProgressSpinner />
            </div>
          </div>
        </div>
      )}

    </Card>
  );
}

export default SurveyListScreen;
